import { MyCompanyPanelView } from '../view/MyCompanyPanelView';
import { templateMyCompanyPanel } from '../view/template';
import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { MyCompanyPanelWebComponent } from '../component/MyCompanyPanelWebComponent';
import { CompanyData } from '../../../types/company-data';
import { MyCompanyPanelRepository } from '../repository/MyCompanyPanelRepository';
import { CompanyDetailsTabEvents } from '../../../common/companyDetailsTab/types/CompanyDetailsTabEvents';
import { OnCompanyPanelClose } from '../../../common/events/CompanyPanelEvent';
import {
  CompanyDetailsEvents,
  OnManageByAboveChange,
} from '../../../common/companyDetailsForm/types/CompanyDetailsEvents';
import { LicensingTabEvents } from '../../../common/licensingTab/LicensingEnums';
import { MyCompanyTabIndex, PurchaseTabParams } from '../../types/types';
import { SwitchValue } from '../../purchase/purchasePage/types';
import { HomePageEventsName } from '../../../../productTrials/b2b/gzTrialHomePage/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { AvailableProductTrialType } from '../../../../productTrials/b2b/types/AvailableProductTrialType';

export class MyCompanyPanelController extends BaseWebComponentController {
  public component: MyCompanyPanelWebComponent;
  public view: MyCompanyPanelView;
  public viewConstructor: typeof MyCompanyPanelView;
  public dataRepository: MyCompanyPanelRepository;
  public dataRepositoryConstructor: typeof MyCompanyPanelRepository;

  constructor(component: MyCompanyPanelWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: MyCompanyPanelView,
      viewTemplate: templateMyCompanyPanel,
      dataRepositoryConstructor: MyCompanyPanelRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$companyDetails,
        event: CompanyDetailsTabEvents.Cancel,
        callback: this.emitCloseCompanyWindowEvent,
      },
      {
        element: this.view.dom.$companyDetails,
        event: CompanyDetailsEvents.ManageByAboveChange,
        callback: this.onCanBeManagedByAboveChange,
      },
      {
        element: this.view.dom.$licensing,
        event: LicensingTabEvents.LicensingGridProductBuyClick,
        callback: this.handleLicenseGridProductBuyEvent,
      },
      {
        element: this.view.dom.$licensing,
        event: LicensingTabEvents.LicensingTabRefresh,
        callback: this.handleLicensingTabRefresh,
      },
      {
        element: this.view.dom.$plansAndPurchase,
        event: HomePageEventsName.PRODUCT_ITEM,
        callback: this.handleClickOnProductToBuy,
      },
    ];
  }

  async getDataForView(): Promise<{ company: CompanyData }> {
    return {
      company: await this.dataRepository.getMyCompanyData(),
    };
  }

  public selectTab(tabId: number, tabParams: any) {
    this.view.dom.$tabGroup.selectTab(tabId);

    if (tabParams) {
      this.processTabParams(tabId, tabParams);
    }
  }

  private processTabParams(tabId, tabParams): void {
    switch (tabId) {
      case MyCompanyTabIndex.PlansAndPurchase:
        this.processPlansAndPurchaseParams(tabParams);
        break;
    }
  }

  private processPlansAndPurchaseParams(params: PurchaseTabParams): void {
    if (this.view.dom.$plansAndPurchase.hasErrorState()) {
      this.view.dom.$plansAndPurchase.refresh();
    }

    if (params.productId) {
      this.view.dom.$plansAndPurchase.setSelectedProduct(
        parseInt(params.productId, 10),
        params.extend ?? SwitchValue.ExtendSeats
      );
    }
  }

  private emitCloseCompanyWindowEvent() {
    this.component.dispatchEvent(new OnCompanyPanelClose());
  }

  private onCanBeManagedByAboveChange(event: OnManageByAboveChange) {
    this.view.dom.$licensing.updateGeneralAddProductViewData(event.detail.canBeManagedByAbove);
  }

  private handleLicenseGridProductBuyEvent(event: CustomEvent, switchValue?: SwitchValue) {
    const productId = event.detail.productId;

    let params;
    if (productId || switchValue) {
      params = {};
      if (productId) {
        params.productId = productId;
      }
      if (switchValue) {
        params.extend = switchValue;
      }
    }

    NavigationHelper.navigateTo('/my-company/purchase', params);
  }

  private handleLicensingTabRefresh(event: CustomEvent) {
    const company = event.detail.updatedCompany;

    this.component.refreshCompanyDetailsTab(company);

    if (this.view.dom.$plansAndPurchase.started) {
      this.view.dom.$plansAndPurchase.refresh();
    }
  }

  private handleClickOnProductToBuy(event: CustomEvent<{ type: AvailableProductTrialType }>) {
    const trialType = event.detail.type;

    this.emitCloseCompanyWindowEvent();

    // navigate to the product page
    NavigationHelper.navigateToProductsHub(trialType);
  }
}
